/**
 * Makes a caller-supplied accessible name authoritative over the one ECharts
 * generates for itself.
 *
 * `ChartSurface` renders `<div role="img" aria-label={label}>` and then hands
 * that same element to `echarts.init()`. ECharts owns the element from then on,
 * and its own accessibility pass writes an `aria-label` describing the whole
 * dataset — "This is a chart about "Alerts and cases by day". It consists of 2
 * series count…" — straight over the React-rendered attribute.
 *
 * Which name a screen reader (or a test) observes therefore depends on whether
 * ECharts' update has landed yet, and that update is deferred by `lazyUpdate`.
 * The result is an accessible name that is racy rather than wrong-but-stable:
 * it passed locally and failed on a slower CI runner, on identical code.
 *
 * Rather than fight ECharts for the attribute — disabling its aria support
 * would also discard the data description, which is genuinely useful — this
 * tells ECharts what to write. `aria.label.description` replaces the generated
 * text, so ECharts sets exactly the caller's label and both writers agree.
 *
 * With no label supplied there is nothing to preserve, so ECharts' generated
 * description is left alone as the better default.
 */
import type { EChartsOption } from 'echarts';

export function withAccessibleName(option: EChartsOption, label: string | undefined): EChartsOption {
  if (label === undefined || label === '') return option;

  return {
    ...option,
    aria: {
      ...option.aria,
      enabled: true,
      label: {
        ...option.aria?.label,
        enabled: true,
        description: label,
      },
    },
  };
}
